"use client";

import { useCallback } from "react";
import z from "zod";
import { emitAppEvent } from "../../events";
import { ConsentAcceptedEvent } from "../../events/consent-accepted.event";
import { ConsentNotShowedEvent } from "../../events/consent-not-showed.event";
import { ConsentRejectedEvent } from "../../events/consent-rejected.event";
import { getCmpData } from "./utils";

const cmpEvents = z.enum(["consentcustom", "consentapproved", "consentrejected", "loadNotShowing"]);

// `userChoiceExists`, not `consentExists` - CMP reports consentExists before the user answers the banner.
function hasStoredUserChoice(cmp: NonNullable<Window["__cmp"]>) {
  try {
    const data = cmp("getCMPData") as { userChoiceExists?: boolean } | undefined;
    return data?.userChoiceExists === true;
  } catch {
    return false;
  }
}

export function useCmpEventListener() {
  const handleCmpEvent = useCallback((event: unknown) => {
    switch (event) {
      case cmpEvents.enum.loadNotShowing: {
        const cmpData = getCmpData();
        if (!cmpData) return;

        emitAppEvent(ConsentNotShowedEvent, cmpData);
        break;
      }
      case cmpEvents.enum.consentcustom:
      case cmpEvents.enum.consentapproved: {
        const cmpData = getCmpData();
        if (!cmpData) return;

        emitAppEvent(ConsentAcceptedEvent, cmpData);
        break;
      }
      case cmpEvents.enum.consentrejected: {
        const cmpData = getCmpData();
        if (!cmpData) return;

        emitAppEvent(ConsentRejectedEvent, cmpData);
        break;
      }
    }
  }, []);

  const initializeCmpListeners = useCallback(() => {
    const cmp = window.__cmp;
    if (!cmp) return;

    cmpEvents.options.forEach((event) => {
      cmp(
        "addEventListener",
        [
          event,
          () => {
            handleCmpEvent(event);
          },
          false,
        ],
        null,
      );
    });

    // CMP never replays events to late listeners. On a refresh it can fire loadNotShowing
    // before React mounts us, so catch up from its stored choice. While CMP is still the stub
    // (`a` queue), the real CMP picks up the listeners above and nothing is missed.
    if (!cmp.a && hasStoredUserChoice(cmp)) {
      handleCmpEvent(cmpEvents.enum.loadNotShowing);
    }
  }, [handleCmpEvent]);

  return { initializeCmpListeners };
}
